import { Close, Watch } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Button,
} from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { isMobile } from "react-device-detect";
import { useSessionContext } from "@/contexts/SessionContext";
import { BundlePatientSummary } from "@/services/fhir";
import {
  formatWearableObservationValue,
  getSleepSkinTempDisplay,
  getSleepTotalDisplay,
} from "@/utils/wearableObservationFormat";
import { useFhirData } from "@/utils/useFhirData";
import { PageHeader } from "@/components/common/PageHeader";
import { FormDatePicker } from "@/components/form/FormDatePicker";
import { useForm } from "react-hook-form";

interface WearableObservationsDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function WearableObservationsDialog({
  open,
  setOpen,
}: WearableObservationsDialogProps) {
  const { user } = useSessionContext();

  const { control, watch } = useForm({
    defaultValues: {
      date: dayjs(),
    },
  });

  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<any>();
  const selectedDate = watch("date") as dayjs.Dayjs | undefined;
  const [selectedIssuedTime, setSelectedIssuedTime] = useState<string | "all">(
    "all"
  );

  useEffect(() => {
    async function fetchSummary() {
      if (!user?.username) return;

      try {
        setLoading(true);
        const bundleResponse = await BundlePatientSummary({
          identifier: user.username,
          purposeofuse: "Atendimento",
        });

        setSummaryData(bundleResponse);
      } catch (error) {
        // Erros são tratados pelo próprio serviço (toast, redirects, etc)
      } finally {
        setLoading(false);
      }
    }

    if (open) {
      fetchSummary();
    }
  }, [open, user?.username]);

  const observationEntries = useMemo(() => {
    const observationSummary = useFhirData.extractPatientSummaryResource(
      summaryData,
      "Observation"
    )?.[0];

    return observationSummary?.entry || [];
  }, [summaryData]);

  const filteredObservations = useMemo(() => {
    if (!selectedDate || !dayjs(selectedDate).isValid()) return [];

    const selectedDateKey = dayjs(selectedDate).format("YYYY-MM-DD");

    return observationEntries
      .filter((entry: any) => {
        const r = entry?.resource;
        // Considera effectiveDateTime ou effectivePeriod (ex.: Sono total só tem effectivePeriod)
        const effective = r?.effectiveDateTime ?? r?.effectivePeriod?.start ?? r?.effectivePeriod?.end;
        if (!effective) return false;

        const dateKey = dayjs(effective).format("YYYY-MM-DD");
        return dateKey === selectedDateKey;
      })
      .sort((a: any, b: any) => {
        // Ordena do mais recente para o mais antigo: usa issued, depois effectiveDateTime
        const getSortDate = (entry: any) => {
          const r = entry?.resource;
          const issued = r?.issued;
          if (issued) return dayjs(issued).valueOf();
          const effective = r?.effectiveDateTime;
          if (effective) return dayjs(effective).valueOf();
          const periodStart = r?.effectivePeriod?.start;
          if (periodStart) return dayjs(periodStart).valueOf();
          return dayjs(r?.meta?.lastUpdated).valueOf() || 0;
        };
        return getSortDate(b) - getSortDate(a);
      });
  }, [observationEntries, selectedDate]);

  // Agrupa as observações por horário de envio (issued) em HH:mm
  const issuedGroups = useMemo(
    () => {
      const groups: Record<string, any[]> = {};

      filteredObservations.forEach((entry: any) => {
        const issued = entry?.resource?.issued;
        if (!issued) return;

        const timeKey = dayjs(issued).format("HH:mm");
        if (!groups[timeKey]) {
          groups[timeKey] = [];
        }
        groups[timeKey].push(entry);
      });

      return Object.entries(groups)
        .map(([time, items]) => ({ time, items }))
        .sort((a, b) =>
          dayjs(b.time, "HH:mm").diff(dayjs(a.time, "HH:mm"))
        );
    },
    [filteredObservations]
  );

  // Sempre que a lista de grupos mudar (nova data, novos dados), seleciona o primeiro horário ou "all"
  useEffect(() => {
    if (issuedGroups.length > 0) {
      setSelectedIssuedTime(issuedGroups[0].time);
    } else {
      setSelectedIssuedTime("all");
    }
  }, [issuedGroups]);

  // Lista final de observações a serem exibidas, filtrada pelo horário selecionado (issued)
  const observationsForSelectedTime = useMemo(() => {
    if (selectedIssuedTime === "all") return filteredObservations;

    const group = issuedGroups.find((g) => g.time === selectedIssuedTime);
    return group?.items ?? [];
  }, [filteredObservations, issuedGroups, selectedIssuedTime]);

  return (
    <Dialog
      PaperProps={{
        sx: {
          borderRadius: "10px",
          padding: 2,
          height: isMobile ? "100%" : "fit-content",
          maxHeight: isMobile ? "none" : "95vh",
        },
      }}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      onClose={() => setOpen(false)}
      open={open}
    >
      <IconButton
        onClick={() => setOpen(false)}
        sx={{ position: "absolute", right: 5, top: 5 }}
      >
        <Close />
      </IconButton>

      <DialogContent>
        <Stack width="100%" spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Watch sx={{ fontSize: 26, color: "primary.main" }} />
                <Typography variant="h6" fontWeight={600}>
                  Dispositivos (Wearable)
                </Typography>
              </Stack>
              <Typography variant="body2" color="neutral600.main">
                Visualize as medições dos seus dispositivos
              </Typography>
            </Stack>

            <Box sx={{ minWidth: 220, ml: 2 }}>
              <FormDatePicker
                name="date"
                control={control}
                label="Dia da medição"
                required={false}
                maxDate={dayjs()}
              />
            </Box>
          </Stack>

          {loading ? (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ minHeight: 200 }}
            >
              <CircularProgress />
            </Stack>
          ) : filteredObservations.length === 0 ? (
            <Stack spacing={3} alignItems="center" sx={{ mt: 2 }}>
              <Stack spacing={0.5} alignItems="center">
                <Typography variant="subtitle1" fontWeight={600}>
                  Nenhum dado encontrado para o dia selecionado.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Você ainda não possui registros de dispositivos conectados para esta data.
                </Typography>
              </Stack>

              <Card
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "primary100.main",
                  backgroundColor: "primary50.main",
                  maxWidth: 540,
                  width: "100%",
                }}
              >
                <CardContent>
                  <Stack spacing={1.75}>
                    <Typography variant="body2" color="text.secondary">
                      Para visualizar seus dados aqui, é necessário ter um relógio inteligente
                      conectado ao <strong>Samsung Health</strong> e seguir os passos abaixo:
                    </Typography>

                    <Stack spacing={1.25}>
                      <Stack direction="row" spacing={1.25} alignItems="flex-start">
                        <Typography
                          variant="body2"
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            bgcolor: "primary100.main",
                            color: "primary800.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 600,
                            fontSize: 12,
                            mt: 0.25,
                          }}
                        >
                          1
                        </Typography>
                        <Typography variant="body2">
                          Instale o aplicativo <strong>Fluxhealth</strong> no seu smartphone
                          e faça login com a mesma conta utilizada no Fluxmed.
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1.25} alignItems="flex-start">
                        <Typography
                          variant="body2"
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            bgcolor: "primary100.main",
                            color: "primary800.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 600,
                            fontSize: 12,
                            mt: 0.25,
                          }}
                        >
                          2
                        </Typography>
                        <Typography variant="body2">
                          No aplicativo <strong>Fluxhealth</strong>, ative as permissões de
                          acesso aos dados do <strong>Samsung Health</strong> (passos, sono,
                          frequência cardíaca, etc.).
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1.25} alignItems="flex-start">
                        <Typography
                          variant="body2"
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            bgcolor: "primary100.main",
                            color: "primary800.main",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: 600,
                            fontSize: 12,
                            mt: 0.25,
                          }}
                        >
                          3
                        </Typography>
                        <Typography variant="body2">
                          Envie seus dados para o médico pelo <strong>Fluxhealth</strong> e,
                          depois disso, retorne a esta tela para acompanhar as medições do período.
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          ) : (
            <Stack
              sx={{
                maxHeight: 580,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
              }}
            >
              {(() => {
                const sleepAndSpO2: any[] = [];
                const general: any[] = [];

                const sourceObservations = observationsForSelectedTime;

                sourceObservations.forEach((entry: any) => {
                  const resource = entry?.resource;
                  const code = resource?.code?.coding?.[0]?.code;
                  const text =
                    resource?.code?.text ||
                    resource?.code?.coding?.[0]?.display ||
                    "";
                  const lower = text.toLowerCase();
                  const isSleepOrSpO2 =
                    lower.startsWith("sono") || lower.startsWith("spo2");
                  const isSkinTemp = code === "81264-4";

                  if (isSleepOrSpO2 || isSkinTemp) {
                    sleepAndSpO2.push(entry);
                  } else {
                    general.push(entry);
                  }
                });

                // Temperatura corporal por último na seção de sono
                sleepAndSpO2.sort((a: any, b: any) => {
                  const codeA = a?.resource?.code?.coding?.[0]?.code;
                  const codeB = b?.resource?.code?.coding?.[0]?.code;
                  if (codeA === "81264-4") return 1;
                  if (codeB === "81264-4") return -1;
                  return 0;
                });

                const renderSection = (title: string, items: any[]) => {
                  if (!items.length) return null;

                  return (
                    <Stack key={title} spacing={1.5}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          px: 1,
                          py: 0.75,
                          borderRadius: 1.5,
                          backgroundColor:
                            title === "Dados objetivos de sono"
                              ? "primary100.main"
                              : "neutral200.main",
                          border: "1px solid",
                          borderColor:
                            title === "Dados objetivos de sono"
                              ? "primary100.main"
                              : "neutral200.main",
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            sx={{
                              width: 6,
                              height: 20,
                              borderRadius: 2,
                              bgcolor:
                                title === "Dados objetivos de sono"
                                  ? "primary.main"
                                  : "neutral500.main",
                            }}
                          />
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            color="neutral900.main"
                          >
                            {title}
                          </Typography>
                        </Stack>
                      </Box>

                      <Stack
                        sx={{
                          display: "grid",
                          gridTemplateColumns: {
                            xs: "1fr",
                            sm: "1fr 1fr",
                            md: "1fr 1fr 1fr",
                          },
                          gap: 1.5,
                          alignItems: "start",
                        }}
                      >
                        {items.map((entry: any) => {
                          const resource = entry?.resource;
                          const code = resource?.code?.coding?.[0]?.code;
                          let label =
                            resource?.code?.text ||
                            resource?.code?.coding?.[0]?.display ||
                            "Observação";

                          if (label === "SpO2 Tempo abaixo de 90%") {
                            label = "SpO2 abaixo de 90%";
                          }
                          if (label === "Calorias (Consumidas)") {
                            label = "Calorias";
                          }

                          // Valor formatado padrão (genérico)
                          const value = formatWearableObservationValue(resource);

                          // Cartão Sono Total: 1ª duração; 2ª Início → Fim; 3ª observação (note) em linha pequena
                          if (code === "93832-4") {
                            const { durationText, noteText, start, end } =
                              getSleepTotalDisplay(resource);

                            const hasNote = Boolean(noteText?.trim());
                            const line2 = start && end ? `Início: ${start} → Fim: ${end}` : null;

                            return (
                              <Card
                                key={resource?.id}
                                sx={{
                                  borderRadius: 2,
                                  boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
                                  border: "1px solid",
                                  borderColor: "neutral200.main",
                                  minWidth: 220,
                                }}
                              >
                                <CardContent
                                  sx={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "flex-start",
                                    justifyContent: "space-between",
                                    gap: 1,
                                    py: 0.6,
                                    minHeight: 60,
                                    "&:last-child": { pb: 0.8 },
                                  }}
                                >
                                  <Typography
                                    fontWeight={500}
                                    variant="body2"
                                    sx={{ flex: 1, minWidth: 0 }}
                                  >
                                    {label}
                                  </Typography>
                                  <Stack
                                    spacing={0.25}
                                    alignItems="flex-end"
                                    sx={{ flexShrink: 0, maxWidth: "60%" }}
                                  >
                                    <Typography
                                      variant="body2"
                                      fontWeight={600}
                                      sx={{ color: "primary800.main", textAlign: "right" }}
                                    >
                                      {durationText}
                                    </Typography>
                                    {line2 && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ textAlign: "right", whiteSpace: "nowrap" }}
                                      >
                                        {line2}
                                      </Typography>
                                    )}
                                    {hasNote && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{
                                          textAlign: "right",
                                          fontSize: "0.7rem",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {noteText!.trim()}
                                      </Typography>
                                    )}
                                  </Stack>
                                </CardContent>
                              </Card>
                            );
                          }

                          // Cartão Temperatura corporal: título "Temperatura corporal: Média X" e abaixo observação em cinza (mín e máx com horário)
                          if (code === "81264-4") {
                            const { minText, avgText, maxText, minTime, maxTime } =
                              getSleepSkinTempDisplay(resource);

                            const minPart = minTime ? `Mín ${minText} (${minTime})` : `Mín ${minText}`;
                            const maxPart = maxTime ? `Máx ${maxText} (${maxTime})` : `Máx ${maxText}`;
                            const observationLine = `${minPart} · ${maxPart}`;

                            return (
                              <Card
                                key={resource?.id}
                                sx={{
                                  borderRadius: 2,
                                  boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
                                  border: "1px solid",
                                  borderColor: "neutral200.main",
                                  minWidth: 220,
                                }}
                              >
                                <CardContent
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.25,
                                    py: 0.6,
                                    minHeight: 60,
                                    "&:last-child": { pb: 0.8 },
                                  }}
                                >
                                  <Typography variant="body2" fontWeight={600} sx={{ color: "text.primary" }}>
                                    Temperatura corporal:{" "}
                                    <Box component="span" sx={{ color: "primary800.main" }}>
                                      Média {avgText}
                                    </Box>
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.primary",
                                      fontSize: "0.7rem",
                                    }}
                                  >
                                    {observationLine}
                                  </Typography>
                                </CardContent>
                              </Card>
                            );
                          }

                          // Cartão padrão para demais observações
                          return (
                            <Card
                              key={resource?.id}
                              sx={{
                                borderRadius: 2,
                                boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
                                border: "1px solid",
                                borderColor: "neutral200.main",
                                minWidth: 220,
                              }}
                            >
                              <CardContent
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  flexWrap: "wrap",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 2,
                                  py: 0.6,
                                  minHeight: 60,
                                  "&:last-child": { pb: 0.8 },
                                }}
                              >
                                <Stack
                                  spacing={0.5}
                                  sx={{ flex: 1, minWidth: 0 }}
                                >
                                  <Typography fontWeight={500}>
                                    {label}
                                  </Typography>
                                </Stack>
                                <Typography
                                  fontWeight={600}
                                  sx={{ color: "primary800.main" }}
                                >
                                  {value}
                                </Typography>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </Stack>
                    </Stack>
                  );
                };

                return (
                  <>
                    {issuedGroups.length > 0 && (
                      <Stack
                        direction="row"
                        spacing={1}
                        flexShrink={0}
                        sx={{
                          minHeight: 40,
                          mb: 1.5,
                          pb: 0.5,
                          overflowX: "auto",
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                          backgroundColor: "background.paper",
                          py: 0.5,
                        }}
                      >
                        {issuedGroups.map((group) => (
                          <Button
                            key={group.time}
                            size="small"
                            variant={
                              selectedIssuedTime === group.time
                                ? "contained"
                                : "outlined"
                            }
                            color="primary"
                            onClick={() => setSelectedIssuedTime(group.time)}
                            sx={{ flexShrink: 0 }}
                          >
                            {group.time}
                          </Button>
                        ))}
                      </Stack>
                    )}

                    {renderSection("Dados objetivos gerais", general)}
                    {renderSection("Dados objetivos de sono", sleepAndSpO2)}
                  </>
                );
              })()}
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

